import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const DonationSection = () => {
  const [selectedAmount, setSelectedAmount] = useState(50);
  const [customAmount, setCustomAmount] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const predefinedAmounts = [25, 50, 100, 250, 500];

  const handleAmountSelect = (amount: number) => {
    setSelectedAmount(amount);
    setCustomAmount("");
  };

  const handleCustomAmount = (value: string) => {
    setCustomAmount(value);
    setSelectedAmount(0);
  };

  const handleDonation = async () => {
    const amount = customAmount ? parseFloat(customAmount) : selectedAmount;
    
    try {
      const { donationSchema } = await import('@/lib/validations');
      donationSchema.parse({ amount });
    } catch (error: any) {
      toast({
        title: "Ungültiger Betrag",
        description: error?.issues?.[0]?.message || "Bitte wählen oder geben Sie einen gültigen Spendenbetrag ein.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('create-payment', {
        body: { 
          amount: Math.round(amount * 100)
        }
      });

      if (error) throw error;
      
      if (data?.url) {
        window.open(data.url, '_blank');
      } else {
        throw new Error('Zahlungslink konnte nicht erstellt werden');
      }
    } catch (error) {
      console.error('Donation error:', error);
      toast({
        title: "Fehler bei der Spendenverarbeitung",
        description: "Ihre Spende konnte nicht verarbeitet werden. Bitte versuchen Sie es erneut.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section id="donation-section" className="py-20 bg-gradient-primary relative overflow-hidden">
      <div className="absolute inset-0 bg-primary/5"></div>
      <div className="container mx-auto px-4 relative">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
            Ihre Spende 
            <span className="text-accent-orange"> rettet Leben</span>
          </h2>
          <p className="text-xl text-white/90 max-w-3xl mx-auto leading-relaxed">
            Jede Spende bringt uns einen Schritt näher daran, Heilmittel für neurologische Erkrankungen zu finden. 
            Ihre Unterstützung macht unsere Forschung möglich.
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          <Card className="shadow-strong border-0 backdrop-blur-sm bg-card/95 dark:bg-card/95">
            <CardHeader>
              <CardTitle className="text-2xl text-center text-foreground">
                Eine Spende Tätigen
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <label className="text-sm font-medium text-muted-foreground mb-3 block">
                  Wählen Sie einen Betrag:
                </label>
                <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                  {predefinedAmounts.map((amount) => (
                    <Button
                      key={amount}
                      variant={selectedAmount === amount ? "default" : "outline"}
                      onClick={() => handleAmountSelect(amount)}
                      className={`py-3 ${
                        selectedAmount === amount 
                          ? "bg-primary hover:bg-primary-dark text-white shadow-medium" 
                          : "hover:bg-primary/10 hover:border-primary"
                      }`}
                    >
                      €{amount}
                    </Button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-muted-foreground mb-2 block">
                  Oder geben Sie einen benutzerdefinierten Betrag ein:
                </label>
                <Input
                  type="number"
                  placeholder="Betrag in EUR"
                  value={customAmount}
                  onChange={(e) => handleCustomAmount(e.target.value)}
                  className="text-lg py-3"
                />
              </div>

              <div className="bg-accent p-4 rounded-lg">
                <p className="text-sm text-muted-foreground text-center">
                  Mit <span className="font-semibold text-foreground">
                    €{customAmount || selectedAmount}
                  </span> können wir finanzieren{" "}
                  {customAmount || selectedAmount >= 250 
                    ? "eine vollständige Woche Laborforschung" 
                    : customAmount || selectedAmount >= 100
                    ? "fortgeschrittene Analysen neurologischer Proben"
                    : "wesentliche Materialien für unsere Experimente"
                  }
                </p>
              </div>

              <Button 
                size="lg" 
                className="w-full bg-secondary hover:bg-secondary-light text-white py-4 text-lg font-semibold shadow-medium transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleDonation}
                disabled={isLoading}
              >
                {isLoading ? "Wird verarbeitet..." : `€${customAmount || selectedAmount} Spenden`}
              </Button>

              <div className="mt-6 p-4 bg-muted/50 rounded-lg text-left space-y-4">
                <h4 className="font-semibold text-foreground text-sm">Entdecken Sie alle Vorteile einer Spende an die NeuroResearch Foundation</h4>
                
                <p className="text-xs text-muted-foreground">
                  Ob als Privatperson oder als juristische Person, Ihre Spenden an die NeuroResearch Foundation können nur in Spanien von der Einkommensteuer oder Körperschaftsteuer abgesetzt werden. Unsere Organisation unterliegt dem Gesetz 49/2002 über das Steuerregime für gemeinnützige Einrichtungen und Anreize für Mäzenatentum. Darüber hinaus hat das Königliche Gesetzesdekret 6/2023 die Abzüge für Spenden an NGOs verbessert, sodass eine Zusammenarbeit nun noch mehr Vorteile bietet.
                </p>

                <div>
                  <h5 className="font-medium text-foreground text-xs mb-1">Abzüge für Privatpersonen (Einkommensteuer)</h5>
                  <p className="text-xs text-muted-foreground mb-2">Der Prozentsatz, den Sie absetzen können, hängt vom Betrag ab und davon, ob Sie kontinuierlich spenden:</p>
                  <ul className="text-xs text-muted-foreground list-disc list-inside space-y-1">
                    <li>Bis zu 250 € pro Jahr → 80% Abzug.</li>
                    <li>Betrag über 250 € → 40% Abzug.</li>
                    <li>Wenn Sie 3 Jahre oder länger spenden und Ihren Beitrag beibehalten oder erhöhen → erhöht sich der Prozentsatz auf den Teil über 250 € auf 45%.</li>
                  </ul>
                </div>

                <div>
                  <h5 className="font-medium text-foreground text-xs mb-1">Praktische Beispiele:</h5>
                  <ul className="text-xs text-muted-foreground list-disc list-inside space-y-1">
                    <li>Wenn Sie 250 € spenden, erstattet Ihnen das Finanzamt 200 €, Ihre tatsächlichen Kosten betragen also 50 €.</li>
                    <li>Wenn Sie 400 € spenden: Erste zwei Jahre → 260 € Abzug.</li>
                    <li>Ab dem dritten Jahr → 267,5 € Abzug.</li>
                  </ul>
                  <p className="text-xs text-muted-foreground mt-2 italic">
                    Denken Sie daran: Wenn Ihre autonome Gemeinschaft zusätzliche Abzüge oder eigene regionale Regelungen hat, kann der Steuervorteil noch größer sein.
                  </p>
                </div>

                <div>
                  <h5 className="font-medium text-foreground text-xs mb-1">Abzüge für Unternehmen (Körperschaftsteuer)</h5>
                  <ul className="text-xs text-muted-foreground list-disc list-inside space-y-1">
                    <li>40% allgemeiner Abzug.</li>
                    <li>50% bei wiederkehrenden Spenden (drei Jahre oder mehr).</li>
                    <li>Grenze: 15% der Bemessungsgrundlage.</li>
                    <li>Überschreiten Sie die Grenze, kann der Überschuss in den folgenden 10 Jahren angewendet werden.</li>
                  </ul>
                </div>

                <p className="text-xs text-primary font-medium">
                  Wenn Sie uns unterstützen möchten, kontaktieren Sie uns.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mt-16 text-white text-center">
          <div>
            <div className="text-3xl font-bold mb-2">€2,5M</div>
            <div className="text-white/80">Gesammelt in 2025</div>
          </div>
          <div>
            <div className="text-3xl font-bold mb-2">1.200+</div>
            <div className="text-white/80">Aktive Spender</div>
          </div>
          <div>
            <div className="text-3xl font-bold mb-2">95%</div>
            <div className="text-white/80">Geht direkt in die Forschung</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DonationSection;